/**
 * Hooks LeadBeton Préfabriqué Phase 1 (brief SG-LDB-CDC-ENRICHISSEMENT) :
 * Commandes + Moules + Formules béton + Équipes production.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  operationsApi,
  type CommandeListParams,
  type CreateCommandePayload,
  type CreateEquipeProductionPayload,
  type CreateFormuleBetonPayload,
  type CreateMoulePayload,
  type MouleListParams,
  type UpdateCommandePayload,
  type UpdateEquipeProductionPayload,
  type UpdateFormuleBetonPayload,
  type UpdateMoulePayload,
} from '@/api/operations';

const COMMANDES_KEY = ['operations', 'beton', 'commandes']           as const;
const MOULES_KEY    = ['operations', 'beton', 'moules']              as const;
const FORMULES_KEY  = ['operations', 'beton', 'formules-beton']      as const;
const EQUIPES_KEY   = ['operations', 'beton', 'equipes-production']  as const;

// ── Commandes ──────────────────────────────────────────────────────────────

export const useCommandes = (params: CommandeListParams = {}) =>
  useQuery({
    queryKey: [...COMMANDES_KEY, params],
    queryFn:  () => operationsApi.beton.listCommandes(params),
    staleTime: 30_000,
  });

export const useCommande = (id: string | undefined) =>
  useQuery({
    queryKey: [...COMMANDES_KEY, id],
    queryFn:  () => operationsApi.beton.getCommande(id!),
    enabled:  !!id,
  });

export const useCreateCommande = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCommandePayload) => operationsApi.beton.createCommande(payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: COMMANDES_KEY }),
  });
};

export const useUpdateCommande = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCommandePayload }) =>
      operationsApi.beton.updateCommande(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: COMMANDES_KEY }),
  });
};

export const useDeleteCommande = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteCommande(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: COMMANDES_KEY }),
  });
};

// ── Moules ─────────────────────────────────────────────────────────────────

export const useMoules = (params: MouleListParams = {}) =>
  useQuery({
    queryKey: [...MOULES_KEY, params],
    queryFn:  () => operationsApi.beton.listMoules(params),
    staleTime: 60_000,
  });

export const useCreateMouleBeton = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMoulePayload) => operationsApi.beton.createMoule(payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: MOULES_KEY }),
  });
};

export const useUpdateMouleBeton = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMoulePayload }) =>
      operationsApi.beton.updateMoule(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: MOULES_KEY }),
  });
};

export const useDeleteMouleBeton = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteMoule(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: MOULES_KEY }),
  });
};

// ── Formules béton ────────────────────────────────────────────────────────

export const useFormulesBeton = () =>
  useQuery({
    queryKey: FORMULES_KEY,
    queryFn:  () => operationsApi.beton.listFormules(),
    staleTime: 60_000,
  });

export const useCreateFormuleBeton = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateFormuleBetonPayload) => operationsApi.beton.createFormule(payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: FORMULES_KEY }),
  });
};

export const useUpdateFormuleBeton = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFormuleBetonPayload }) =>
      operationsApi.beton.updateFormule(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: FORMULES_KEY }),
  });
};

export const useDeleteFormuleBeton = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteFormule(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: FORMULES_KEY }),
  });
};

// ── Équipes production ────────────────────────────────────────────────────

export const useEquipesProduction = () =>
  useQuery({
    queryKey: EQUIPES_KEY,
    queryFn:  () => operationsApi.beton.listEquipesProduction(),
    staleTime: 60_000,
  });

export const useCreateEquipeProduction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateEquipeProductionPayload) => operationsApi.beton.createEquipeProduction(payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: EQUIPES_KEY }),
  });
};

export const useUpdateEquipeProduction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateEquipeProductionPayload }) =>
      operationsApi.beton.updateEquipeProduction(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: EQUIPES_KEY }),
  });
};

export const useDeleteEquipeProduction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteEquipeProduction(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: EQUIPES_KEY }),
  });
};
